// Hooks del módulo pipeline-constructor — patrón espejo de deals/expo-capture.
// Estado local + refetch manual, sin react-query (consistencia con resto).

import { useCallback, useEffect, useState } from 'react';

import { PipelineConstructorApiError, pipelineConstructorApi } from './api';
import type {
  CoverageZoneDTO,
  InventoryItemDTO,
  LeadDTO,
  LeadDetailDTO,
  LeadFilters,
  RunDTO,
  TriggerRunResult,
} from './types';

function describe(e: unknown): string {
  if (e instanceof PipelineConstructorApiError) return e.message;
  if (e instanceof Error) return e.message;
  return 'Error desconocido';
}

export function useLeads(filters: LeadFilters = {}, pageSize: number = 50) {
  const [leads, setLeads] = useState<LeadDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await pipelineConstructorApi.listLeads(filters, { limit: pageSize, offset });
      setLeads(res);
      setHasMore(res.length === pageSize);
    } catch (e) {
      setError(describe(e));
    } finally {
      setLoading(false);
    }
  }, [
    filters.status, filters.project_type, filters.state, filters.min_score,
    pageSize, offset,
  ]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { leads, loading, error, refetch, offset, setOffset, hasMore };
}

export function useLeadDetail(leadId: number | null) {
  const [detail, setDetail] = useState<LeadDetailDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (leadId === null) {
      setDetail(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await pipelineConstructorApi.getLead(leadId);
      setDetail(res);
    } catch (e) {
      setError(describe(e));
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { detail, loading, error, refetch };
}

export function useDismissLead() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dismiss = useCallback(async (leadId: number, reason: string = '') => {
    setLoading(true);
    setError(null);
    try {
      return await pipelineConstructorApi.dismissLead(leadId, reason);
    } catch (e) {
      setError(describe(e));
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  return { dismiss, loading, error };
}

export function useInventory(includeInactive = false) {
  const [items, setItems] = useState<InventoryItemDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await pipelineConstructorApi.listInventory(includeInactive);
      setItems(res);
    } catch (e) {
      setError(describe(e));
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { items, loading, error, refetch };
}

export function useCoverageZones() {
  const [zones, setZones] = useState<CoverageZoneDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await pipelineConstructorApi.listCoverageZones();
      setZones(res);
    } catch (e) {
      setError(describe(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { zones, loading, error, refetch };
}

export function useRuns(limit = 10) {
  const [runs, setRuns] = useState<RunDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await pipelineConstructorApi.listRuns(limit);
      setRuns(res);
    } catch (e) {
      setError(describe(e));
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  return { runs, loading, error, refetch };
}

export function useTriggerRun() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trigger = useCallback(async (sinceDays = 30): Promise<TriggerRunResult> => {
    setLoading(true);
    setError(null);
    try {
      return await pipelineConstructorApi.triggerRun(sinceDays);
    } catch (e) {
      setError(describe(e));
      throw e;
    } finally {
      setLoading(false);
    }
  }, []);

  return { trigger, loading, error };
}
